import { v4 as uuidv4 } from 'uuid'
import { globalReduxStore } from '../../src/modules/initStore'
import { getCoordinates } from '../../src/redux/reducers'
import { IDB_flowAnswers } from '../IDB/IDB.dexie'
import type { Rpts_ClientId } from '../Rpts/Rpts.types'
import { User_get } from '../User/User.get'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import { createAnswerBase } from '../../../shared/domain/flows/answer.no-deps'
import { generateDefaultAnswers } from '../../../shared/domain/flows/generateDefaultAnswers.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { map, flatten } from '../../../shared/lib/functional/array.no-deps'
import type { KnownUnit } from '../../../app-core/lib/unit/unit'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'
import type { LoopPlan } from '../../../shared/domain/flows/LoopPlan.no-deps'
import type { WorkflowReference } from '../../../shared/domain/workflows/types.no-deps'

export type Flow_Answer = Flows.Answer & {
    uploadState: 'pendingUpload' | 'uploaded'
}

export function Flow_createAnswerBase(
    reportId: Flows.ReportId,
    elementId: Flows.ElementId,
    loopPathIds: LoopPathIds,
    oldAnswer?: Flows.Answer | null,
    dynamicElement?: boolean
) {
    return createAnswerBase({
        reportId,
        userId: User_get().id,
        createUUID: () => uuidv4(),
        getCoordinates: () => {
            const { lat, lon } = getCoordinates(globalReduxStore.getState())
            return { latitude: lat, longitude: lon }
        },
        elementId,
        oldAnswer,
        loopPathIds,
        dynamicElement,
    })
}

export function Flow_generateDefaultAnswers(
    reportId: Flows.ReportId,
    flow: Flows.Flow,
    startDate: Date,
    allUnits: KnownUnit[],
    loopPlan: LoopPlan,
    guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide; workflow: WorkflowReference }>,
    subjectUnitId: number | null = null
) {
    return generateDefaultAnswers({
        flow,
        reportStartDate: startDate,
        createAnswerBase: (elementId, loopPathIds) => Flow_createAnswerBase(reportId, elementId, loopPathIds),
        allUnits,
        loopPlan,
        guideElementsContextMap,
        subjectUnitId,
    })
}

export async function Flow_writeFlowActivityDefaultAnswers(
    clientId: Rpts_ClientId,
    startDate: Date,
    flows: Flows.Flow[],
    allUnits: KnownUnit[],
    loopPlan: LoopPlan
) {
    await pipe(
        flows,
        map((flow) => Flow_generateDefaultAnswers(clientId, flow, startDate, allUnits, loopPlan, new Map())),
        flatten,
        map((answer): Flow_Answer => ({ ...answer, uploadState: 'pendingUpload' })),
        (answers) => IDB_flowAnswers().bulkAdd(answers)
    )
}

export async function Flow_hasUnuploadedAnswers(clientId: Rpts_ClientId) {
    return IDB_flowAnswers()
        .where({ report: clientId, uploadState: 'pendingUpload' })
        .limit(1)
        .count()
        .then((c) => c > 0)
}
